import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { normalizeOptionValue, isVisibleForOptions } from '@theme/aromely-option-tags';

/**
 * @typedef {object} VariantData
 * @property {number} id - Variant id
 * @property {string[]} options - Option values by position
 * @property {number} price - Price in cents
 * @property {number|null} compare_at_price - Compare-at price in cents
 * @property {boolean} available - Whether the variant is purchasable
 * @property {{ src: string }|null} featured_image - Variant image
 */

/**
 * @typedef {object} CardImageData
 * @property {string} src - 240w image URL
 * @property {string} srcset - 120/240/360w srcset string
 * @property {string[]} tags - `optionIndex:value` tag tokens ([] = universal image)
 */

/**
 * Aromely Cross-Sell Variant Modal.
 *
 * Bottom-sheet variant picker opened from a cross-sell card's ADD button
 * (`on:click="#CrossSellVariantModal-{productId}-{sectionId}/open"`).
 * Lives inside the cart drawer's <dialog> but outside the hydration wrapper,
 * so cart section morphs never destroy an open modal.
 *
 * Escape / backdrop events are stopped from propagating so the surrounding
 * cart drawer stays open when the modal closes.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal <dialog>.
 * @property {HTMLImageElement} [headerImage] - Product image in the header.
 * @property {HTMLElement} priceDisplay - Current price text.
 * @property {HTMLElement} compareSeparator - Divider between price and compare-at.
 * @property {HTMLElement} comparePriceDisplay - Strikethrough compare-at price.
 * @property {HTMLElement} discountBadge - "SAVE N%" badge.
 * @property {HTMLElement} qtyStepper - Quantity stepper wrapper.
 * @property {HTMLButtonElement} qtyMinus - Quantity decrement button.
 * @property {HTMLElement} qtyDisplay - Quantity value display.
 * @property {HTMLButtonElement} qtyPlus - Quantity increment button.
 * @property {HTMLButtonElement} addButton - ADD TO CART button.
 * @property {HTMLElement} errorText - Inline error message.
 *
 * @extends {Component<Refs>}
 */
class AromelyVariantModal extends Component {
  requiredRefs = ['dialog'];

  /** @type {VariantData[]} */
  #variants = [];

  /** @type {CardImageData[]} */
  #cardImages = [];

  /** @type {VariantData|null} */
  #selectedVariant = null;

  #quantity = 1;

  /** @type {AbortController} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const variantsScript = this.querySelector('[data-variants-json]');
    if (variantsScript) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent || '[]');
      } catch {
        this.#variants = [];
      }
    }

    const cardImagesScript = this.querySelector('[data-card-images-json]');
    if (cardImagesScript) {
      try {
        this.#cardImages = JSON.parse(cardImagesScript.textContent || '[]');
      } catch {
        this.#cardImages = [];
      }
    }

    this.#selectedVariant = this.#resolveVariant() ?? this.#variants.find((v) => v.available) ?? this.#variants[0] ?? null;

    const { dialog } = this.refs;

    // Escape → animated close; stop it from reaching the cart drawer's
    // keydown handler (which would close the drawer too).
    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
      },
      { signal }
    );

    // Native cancel (e.g. some platforms) → replace with animated close.
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      },
      { signal }
    );

    // Backdrop click closes the modal only — clicks on ::backdrop report the
    // dialog itself as target; stop them so the drawer's outside-click
    // handler doesn't fire.
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target !== dialog) return;
        event.stopPropagation();
        this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** Opens the modal and refreshes derived UI state. */
  open() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    dialog.removeAttribute('data-closing');
    this.#hideError();
    this.#setQuantity(1);
    this.#refreshAvailability();
    dialog.showModal();
  }

  /** Closes the modal with the slide-down animation. */
  close() {
    const { dialog } = this.refs;
    if (!dialog.open || dialog.hasAttribute('data-closing')) return;

    dialog.setAttribute('data-closing', '');
    setTimeout(() => {
      dialog.close();
      dialog.removeAttribute('data-closing');
    }, 250);
  }

  /** Radio change — resolve the variant and update price/labels/availability. */
  onOptionChange() {
    const variant = this.#resolveVariant();
    if (variant) this.#selectedVariant = variant;

    this.#updatePriceDisplay();
    this.#updateValueLabels();
    this.#refreshAvailability();
    this.#updateHeaderImage();
    this.#hideError();
  }

  increaseQty() {
    this.#setQuantity(this.#quantity + 1);
  }

  decreaseQty() {
    if (this.#quantity <= 1) return;
    this.#setQuantity(this.#quantity - 1);
  }

  /** Adds the selected variant to the cart in a single round trip. */
  async addToCart() {
    const variant = this.#selectedVariant;
    const { addButton } = this.refs;
    if (!variant || !addButton || addButton.disabled) return;

    addButton.disabled = true;
    addButton.classList.add('is-loading');
    this.#hideError();

    try {
      const sectionId = this.dataset.sectionId;
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: this.#quantity }],
          ...(sectionId && { sections: sectionId, sections_url: window.location.pathname }),
        }),
      });
      const parsed = await response.json();

      if (!response.ok) {
        this.#showError(parsed?.description || parsed?.message || this.dataset.tError);
        return;
      }

      let itemCount;
      const sectionHtml = sectionId ? parsed.sections?.[sectionId] : undefined;
      if (sectionHtml) {
        const countText = new DOMParser()
          .parseFromString(sectionHtml, 'text/html')
          .querySelector('[ref="cartItemCount"]')?.textContent;
        if (countText) itemCount = parseInt(countText, 10);
      }

      this.dispatchEvent(
        new CartUpdateEvent(parsed, this.id, {
          itemCount,
          source: 'aromely-variant-modal',
          sections: parsed.sections,
        })
      );

      this.close();
    } catch (error) {
      console.error('Variant modal add failed:', error);
      this.#showError(this.dataset.tError);
    } finally {
      addButton.disabled = false;
      addButton.classList.remove('is-loading');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Private helpers                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Reads the checked radio of every option group and finds the matching
   * variant (same algorithm as aromely-pdp.js).
   * @returns {VariantData|undefined}
   */
  #resolveVariant() {
    const selectedOptions = this.#selectedOptions();
    return this.#variants.find((v) =>
      v.options.every((opt, i) => selectedOptions[i] === undefined || opt === selectedOptions[i])
    );
  }

  /** @returns {(string|undefined)[]} Selected option values indexed by option position. */
  #selectedOptions() {
    const selected = [];
    for (const group of this.querySelectorAll('[data-option-group]')) {
      const checked = /** @type {HTMLInputElement|null} */ (
        group.querySelector('input[type="radio"]:checked')
      );
      if (checked) {
        selected[parseInt(checked.dataset.optionIndex ?? '', 10)] = checked.dataset.optionValue;
      }
    }
    return selected;
  }

  #updatePriceDisplay() {
    const variant = this.#selectedVariant;
    if (!variant) return;

    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    const { priceDisplay, compareSeparator, comparePriceDisplay, discountBadge } = this.refs;

    // Mirrors the initial paint in snippets/aromely-cross-sell-variant-modal.liquid.
    // A one-time line is a single price unless a collection promotion is
    // running; that promotion comes off the selling price, so the selling price
    // is what gets struck and the badge quotes the promotion's own percentage.
    // variant.compare_at_price is a catalogue value, never a price a shopper
    // paid, so it is not read here or on any other standard surface.
    const promoRaw = Number.parseFloat(this.dataset.collectionPromo ?? '');
    const promoPct =
      Number.isInteger(promoRaw) && promoRaw >= 1 && promoRaw <= 100 ? promoRaw : 0;

    // Truncate, as Shopify does to a line-level discount amount.
    const final =
      promoPct > 0 ? variant.price - Math.floor((variant.price * promoPct) / 100) : variant.price;
    const compare = promoPct > 0 ? variant.price : 0;

    if (priceDisplay) priceDisplay.textContent = formatMoney(final, format, currency);

    const onSale = compare > final;
    compareSeparator?.classList.toggle('hidden', !onSale);
    comparePriceDisplay?.classList.toggle('hidden', !onSale);
    // The badge swaps display classes — `inline-flex` would win over `hidden`.
    discountBadge?.classList.toggle('hidden', !onSale);
    discountBadge?.classList.toggle('inline-flex', onSale);

    if (onSale) {
      if (comparePriceDisplay) {
        comparePriceDisplay.textContent = formatMoney(compare, format, currency);
      }
      if (discountBadge) {
        // Not translated: "-15%" reads the same in every storefront locale.
        discountBadge.textContent = `-${promoPct}%`;
      }
    }
  }

  /** Updates the muted selected-value labels next to option names. */
  #updateValueLabels() {
    const selectedOptions = this.#selectedOptions();
    for (const label of this.querySelectorAll('[data-option-value-label]')) {
      const index = parseInt(label.getAttribute('data-option-value-label') ?? '', 10);
      const value = selectedOptions[index];
      if (value !== undefined) label.textContent = value;
    }
  }

  /**
   * Marks each option value unavailable when no purchasable variant matches
   * it combined with the currently selected values of the OTHER options.
   */
  #refreshAvailability() {
    const selectedOptions = this.#selectedOptions();

    for (const group of this.querySelectorAll('[data-option-group]')) {
      for (const optionEl of group.querySelectorAll('[data-value]')) {
        const radio = /** @type {HTMLInputElement|null} */ (optionEl.querySelector('input[type="radio"]'));
        if (!radio) continue;

        const index = parseInt(radio.dataset.optionIndex ?? '', 10);
        const value = radio.dataset.optionValue;

        const candidate = [...selectedOptions];
        candidate[index] = value;

        const hasAvailable = this.#variants.some(
          (v) =>
            v.available &&
            v.options.every((opt, i) => candidate[i] === undefined || opt === candidate[i])
        );

        optionEl.toggleAttribute('data-unavailable', !hasAvailable);
      }
    }
  }

  /**
   * Swaps the header image: card_image tagged for the selected variant's
   * options first ([tag] alt convention, see aromely-card-image-pick), then
   * the variant's featured image.
   */
  #updateHeaderImage() {
    const { headerImage } = this.refs;
    if (!headerImage) return;

    const cardImage = this.#pickCardImage();
    if (cardImage) {
      headerImage.src = cardImage.src;
      headerImage.srcset = cardImage.srcset;
      return;
    }

    const image = this.#selectedVariant?.featured_image;
    if (!image?.src) return;

    const sized = (width) =>
      image.src.includes('?') ? `${image.src}&width=${width}` : `${image.src}?width=${width}`;
    headerImage.src = sized(240);
    headerImage.srcset = `${sized(120)} 120w, ${sized(240)} 240w, ${sized(360)} 360w`;
  }

  /**
   * First card_image tagged for the selected variant's options, else the
   * first universal (untagged) one. Mirrors the server-side 'prefer-match'
   * pick in snippets/aromely-card-image-pick.liquid.
   *
   * With [data-card-images-native] the blob is the product's own images
   * rather than the card_images metafield, and the server picked with
   * 'match-only' — so drop the universal fallback here too and let the caller
   * fall through to the variant's featured image.
   * @returns {CardImageData|null}
   */
  #pickCardImage() {
    if (!this.#cardImages.length) return null;

    const optionHandles = (this.#selectedVariant?.options ?? []).map(normalizeOptionValue);
    const match =
      optionHandles.length > 0 &&
      this.#cardImages.find((img) => img.tags.length > 0 && isVisibleForOptions(img.tags, optionHandles));
    if (match) return match;
    if (this.hasAttribute('data-card-images-native')) return null;

    return this.#cardImages.find((img) => img.tags.length === 0) || null;
  }

  /** @param {number} value */
  #setQuantity(value) {
    this.#quantity = Math.max(1, value);
    const { qtyDisplay, qtyStepper } = this.refs;
    if (qtyDisplay) qtyDisplay.textContent = String(this.#quantity);
    qtyStepper?.toggleAttribute('data-at-min', this.#quantity <= 1);
  }

  /** @param {string} [message] */
  #showError(message) {
    const { errorText } = this.refs;
    if (!errorText) return;
    errorText.textContent = message || this.dataset.tError || '';
    errorText.classList.remove('hidden');
  }

  #hideError() {
    this.refs.errorText?.classList.add('hidden');
  }
}

if (!customElements.get('aromely-variant-modal')) {
  customElements.define('aromely-variant-modal', AromelyVariantModal);
}
